/**
 * Nivel em que a descricao do vendedor deve comecar.
 *
 * O bloco "Informacoes do Produto" e um h2 na pagina, entao o conteudo dentro
 * dele comeca em h3. Medido na PDP de producao: o vendedor escrevia h4 direto
 * abaixo desse h2, criando um salto h2 -> h4 no unico trecho da pagina que e
 * conteudo de verdade.
 */
export const NIVEL_BASE_DESCRICAO = 3

const ABERTURA_DE_HEADING = /<h([1-6])[^>]*>/gi
const QUALQUER_HEADING = /<(\/?)h([1-6])([^>]*)>/gi

/**
 * Reancora os headings de um HTML de terceiro sem achatar a estrutura dele.
 *
 * O texto vem do vendedor e cada um escreve como quer — um comeca em h1, outro
 * em h4. Fixar todos num nivel unico destruiria a hierarquia interna; o que
 * importa e a posicao RELATIVA. Entao o heading mais raso do trecho vira
 * `base` e os demais descem na mesma proporcao, ate o limite de h6.
 *
 * Isso tambem elimina um defeito mais serio que o salto: vendedor que escreve
 * <h1> na descricao criava um SEGUNDO h1 na pagina, competindo com o titulo do
 * produto.
 */
export const normalizarNiveisDeHeading = (
  html: string,
  base: number = NIVEL_BASE_DESCRICAO
): string => {
  const niveis = Array.from(html.matchAll(ABERTURA_DE_HEADING), ([, nivel]) =>
    Number(nivel)
  )

  if (niveis.length === 0) return html

  const deslocamento = base - Math.min(...niveis)

  if (deslocamento === 0) return html

  return html.replace(
    QUALQUER_HEADING,
    (_, barra: string, nivel: string, resto: string) => {
      const novo = Math.min(6, Number(nivel) + deslocamento)

      /**
       * O nivel original vai junto porque o navegador dimensiona heading por
       * tag: h3 rende 1.17em e h4 rende 1em. Trocar o nivel sem isto aumenta
       * o texto da descricao — medido, 14px viraram 16,38px. O atributo deixa
       * o CSS devolver o tamanho de origem, entao a semantica muda e a
       * aparencia nao.
       */
      return barra
        ? `</h${novo}>`
        : `<h${novo}${resto} data-nivel-visual="${nivel}">`
    }
  )
}

export const formatHtmlContent = (html: string): string => {
  return normalizarNiveisDeHeading(html)
    .replace(/<p[^>]*>\s*<br\s*\/?>\s*<\/p>/g, '<br />')
    .replace(/<\/p>/g, '</p><br />')
}
